import dynamic from 'next/dynamic';
import React, { type Ref, useEffect, useRef, useState } from 'react';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { BADGES_LAYOUT, ResetPosition } from '@/app/components/search/components/NoResults/BadgesContainer/types.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { useSearchCommonPageData } from '@/app/components/search/searchCommonPageProvider.tsx';
import { isSustainabilityAvailable } from '@/app/components/search/components/Banners/SustainabilityBanner/sustainabilityUtils.ts';
import { ViewMode } from '@/app/common/viewModeUtils.ts';
import Form, { type UpdateOnChangeProps } from '../../../Form';
import useOnScreen from '../../../ShowMore/useOnScreen';
import ViewModeToggle from '../ViewModeToggle';
import verticalFilters from '../../../Filters/verticalFilters';
import { getSearchParamsClient } from '../../../../common/getSearchParams';
import type { RouteType } from '../../../../Curiosity';
import { removeSearchParams } from '../../../Filters/utils';
import { handleVerticalFiltersUpdated } from '../../../Filters/verticalFilters/verticalFiltersUpdateOnChange';
import { LOCAL_STORAGE_KEY, localStorageExists } from '../../../../utils/localStorage';
import { SidebarContainer } from './styled';
import type { DynamicFiltersResponse } from '../../../../utils/api/dynamicFiltersResponseTypes';

const BadgesContainer = dynamic(() => import('../../../NoResults/BadgesContainer'), { ssr: false });

type SideBarProps = {
  routeType: RouteType;
  isWaiting: boolean;
  productsData: ProductsData;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  verticalFiltersRef: React.MutableRefObject<Form | undefined>;
};

const SideBar = ({ routeType, isWaiting, productsData, dynamicFiltersResponse, verticalFiltersRef }: SideBarProps) => {
  const {
    searchParams: searchParamsServer,
    searchInfoData,
    localization,
    curiosityParams,
    commonData,
    searchInfoDetailData,
  } = usePageWrapperData();
  const { isMobile, starTrack } = useCommonClient();
  const { handleFiltersChanged } = useSearchCommonPageData();
  const { hasPreselectedFlight } = useSearchDealsPageData();
  const { t } = useTranslation();

  const { currency } = localization;
  const { searchMode } = searchInfoData;
  const containerRef = useRef<HTMLDivElement>(undefined);
  const { isVisible } = useOnScreen({ ref: containerRef });
  const searchParams = getSearchParamsClient(searchParamsServer);
  const appliedFilters = removeSearchParams(searchParams);

  const [hasPrefilterAirport, setHasPrefilterAirport] = useState(false);
  useEffect(() => {
    setHasPrefilterAirport(localStorageExists(LOCAL_STORAGE_KEY.PREFILTER_DESTINATION_AIRPORT));
  }, []);

  const verticalControls = verticalFilters({
    searchMode,
    searchParams,
    searchInfoData,
    searchInfoDetailData,
    isMap: true,
    isBeachHoliday: searchParams.destination === 'L1',
    commonData,
    localization,
    currency,
    curiosityParams,
    isSidebar: true,
    isWaiting,
    productsData,
    dynamicFiltersResponse,
    isMobile,
    hasPreselectedFlight,
    hasPrefilterAirport,
    t,
  });

  const sustainabilityEnabled = isSustainabilityAvailable({
    localization,
    productsData,
    searchParams,
    hasPreselectedFlight,
  });

  const updateOnChange = async ({ e, updatedControls }: UpdateOnChangeProps) => {
    handleVerticalFiltersUpdated({
      e,
      appliedFilters,
      commonData,
      localization,
      curiosityParams,
      routeType,
      searchParams,
      productsData,
      handleFiltersChanged,
      starTrack,
      searchInfoDetailData,
      t,
      dynamicFiltersResponse,
    });
    return updatedControls;
  };

  return (
    <SidebarContainer className="sidebar">
      <ViewModeToggle viewMode={ViewMode.LIST} isWaiting={isWaiting} sustainabilityEnabled={sustainabilityEnabled} />
      <div ref={containerRef as Ref<HTMLDivElement> | undefined}>
        <Form
          key={Math.random()}
          ref={verticalFiltersRef as Ref<Form> | undefined}
          controls={verticalControls}
          updateOnChange={updateOnChange}
          formClassName={'openx-ui-filters openx-ui-modal-filters'}
        />
      </div>
      {!isVisible && (
        <BadgesContainer
          resetPosition={ResetPosition.Left}
          isFixed={true}
          dynamicFiltersResponse={dynamicFiltersResponse}
          layout={BADGES_LAYOUT.MULTIPLE_ROWS}
        />
      )}
    </SidebarContainer>
  );
};

export default SideBar;
